// Base del BFF de operaciones (ops-bff). El cliente HTTP (cookies+CSRF) vive en
// @lumal/lib (apiFetch).
import { apiFetch } from '@lumal/lib';
import type { PlatformPermission, PlatformRole } from './types';

export const API = `${location.protocol}//api.${location.host}`;

// Gestión de roles de PLATAFORMA (CRUD completo vía /portal/platform/iam/*). El
// ops-bff hace de proxy a cmo-core; el enforcement anti-escalación vive en el core.
const IAM = `${API}/portal/platform/iam`;

interface Items<T> {
  items: T[];
}

function csrf(token: string): Record<string, string> {
  return { 'X-CSRF-Token': token };
}

export const platformIam = {
  listPermissions: () => apiFetch<Items<PlatformPermission>>(`${IAM}/permissions`),
  listRoles: () => apiFetch<Items<PlatformRole>>(`${IAM}/roles`),

  createRole: (
    token: string,
    body: { codename: string; display_name: string; rank: number; permissions: string[] },
  ) => apiFetch<{ role: PlatformRole }>(`${IAM}/roles`, 'POST', { headers: csrf(token), body }),

  updateRole: (
    token: string,
    id: string,
    body: { display_name?: string; rank?: number; permissions?: string[] },
  ) =>
    apiFetch<{ role: PlatformRole }>(`${IAM}/roles/${encodeURIComponent(id)}`, 'PATCH', {
      headers: csrf(token),
      body,
    }),

  deleteRole: (token: string, id: string, reassignTo?: string) => {
    const q = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : '';
    return apiFetch<{ archived: boolean; reassigned_to: string }>(
      `${IAM}/roles/${encodeURIComponent(id)}${q}`,
      'DELETE',
      { headers: csrf(token) },
    );
  },
};

// Traduce los códigos de error de delegación del backend a mensajes claros.
export function iamErrorMessage(e: unknown): string {
  const err = e as { code?: string; message_safe?: string } | undefined;
  switch (err?.code) {
    case 'RANK_ESCALATION':
      return 'No puedes otorgar un rango mayor o igual al tuyo.';
    case 'PERM_ESCALATION':
      return 'No puedes otorgar un permiso que tú mismo no posees.';
    case 'CONFLICT':
      return 'Ya existe un rol con ese codename.';
    case 'ROLE_IN_USE':
      return err.message_safe || 'El rol tiene asignaciones activas: reasigna o revoca antes de eliminarlo.';
    case 'FORBIDDEN':
      return err.message_safe || 'No tienes autoridad para esta acción.';
    case 'NOT_FOUND':
      return err.message_safe || 'Recurso no encontrado.';
    default:
      return err?.message_safe || 'Ocurrió un error.';
  }
}
